import { useMemo, useState } from 'react';
import { db } from '../db/indexedDb';
import { useTracks } from '../hooks/useIndexedDb';
import { useToast } from '../hooks/useToast';
import { seedDemoTracks } from '../lib/demo';
import { extractTrackFromFile } from '../lib/id3';
import type { ExportPayload, ImportJobResult } from '../types';
import { UploadDropzone } from '../components/UploadDropzone';

export function UploadPage() {
  const tracks = useTracks();
  const { pushToast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ImportJobResult[]>([]);

  const importedCount = useMemo(
    () => results.filter((result) => result.status === 'imported').length,
    [results]
  );

  const handleFiles = async (files: File[]) => {
    setIsImporting(true);
    setResults([]);
    let processed = 0;
    const nextResults: ImportJobResult[] = [];

    for (const file of files) {
      try {
        const parsed = await extractTrackFromFile(file);
        const response = await db.importTrack(parsed);
        nextResults.push(
          response.duplicate
            ? {
                status: 'duplicate',
                fileName: file.name,
                message: `${file.name} already exists in your library.`
              }
            : {
                status: 'imported',
                fileName: file.name,
                message: `${parsed.title} was saved for offline playback.`,
                trackId: response.track?.id
              }
        );
      } catch (error) {
        nextResults.push({
          status: 'failed',
          fileName: file.name,
          message:
            error instanceof DOMException && error.name === 'QuotaExceededError'
              ? 'Storage quota reached. Remove some tracks and try again.'
              : 'This file could not be parsed or stored.'
        });
      }
      processed += 1;
      setProgress(Math.round((processed / files.length) * 100));
      setResults([...nextResults]);
    }

    setIsImporting(false);
    pushToast({
      title: 'Import finished',
      description: `${nextResults.filter((item) => item.status === 'imported').length} tracks imported.`
    });
  };

  const exportLibrary = async () => {
    const payload = await db.exportLibrary();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `local-wave-export-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importBackup = async (file: File) => {
    const text = await file.text();
    const payload = JSON.parse(text) as ExportPayload;
    await db.importLibrary(payload);
    pushToast({
      title: 'Backup imported',
      description: 'Metadata, playlists, and app settings were restored.'
    });
  };

  return (
    <section className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.5fr,1fr]">
        <UploadDropzone onFiles={(files) => void handleFiles(files)} disabled={isImporting} />
        <aside className="panel rounded-[2rem] p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-accent">Library health</p>
          <h2 className="mt-3 font-display text-2xl font-semibold">{tracks?.length ?? 0} tracks stored</h2>
          <p className="mt-3 text-sm text-foreground/70">
            Imports stay local to this browser profile. Previously added tracks remain playable
            offline because the audio blobs live in IndexedDB.
          </p>
          {isImporting ? (
            <div className="mt-6">
              <div className="h-3 rounded-full bg-white/10">
                <div className="h-3 rounded-full bg-accent" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-sm text-foreground/70">{progress}% parsed and stored</p>
            </div>
          ) : null}
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" onClick={() => void exportLibrary()} className="rounded-full bg-accent px-4 py-2 font-semibold text-black">
              Export metadata JSON
            </button>
            <label className="pill cursor-pointer">
              Import backup
              <input
                type="file"
                accept="application/json"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void importBackup(file);
                }}
              />
            </label>
            {import.meta.env.DEV ? (
              <button
                type="button"
                onClick={() => void seedDemoTracks()}
                className="pill"
              >
                Seed demo tracks
              </button>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="panel rounded-[2rem] p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Recent import run</p>
            <h3 className="mt-2 font-display text-2xl font-semibold">{importedCount} songs added</h3>
          </div>
          <p className="text-sm text-foreground/65">
            Duplicates are skipped when the SHA-256 hash already exists.
          </p>
        </div>
        <ul className="mt-6 space-y-3">
          {results.length ? (
            results.map((result) => (
              <li key={`${result.fileName}-${result.status}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="font-medium">{result.fileName}</p>
                <p className="mt-1 text-sm text-foreground/70">{result.message}</p>
              </li>
            ))
          ) : (
            <li className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-foreground/70">
              Import results will appear here after you drop files.
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}
